import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { CircleAlert, Download, FileArchive } from 'lucide-react';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { StatusBadge, type Tone } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { readPaging } from '../../lib/paging';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useUrlState } from '../../lib/useUrlState';
import { useAuth } from '../auth/AuthProvider';
import { formatBytes } from '../system/media';
import {
  downloadLink,
  exportsKey,
  listExports,
  type ExportStatus,
  type RecordingExport,
} from './api';
import { browser } from './browser';
import { exportFailure } from './exportFailures';
import { formatRange } from './format';

const statusView: Record<ExportStatus, { label: string; tone: Tone; pulse?: boolean }> = {
  queued: { label: 'Na fila', tone: 'neutral' },
  running: { label: 'Gerando', tone: 'warn', pulse: true },
  ready: { label: 'Pronta', tone: 'ok' },
  failed: { label: 'Falhou', tone: 'crit' },
  expired: { label: 'Expirada', tone: 'neutral' },
};

export function ExportsPanel() {
  const { request, can, session } = useAuth();
  const tz = session?.tenant.timezone;
  const fmt = useTimeFormat();
  const { notify } = useToast();
  const [params, update] = useUrlState();
  const { page, size } = readPaging(params);
  const canDownload = can('recordings.export');

  const exports = useQuery({
    queryKey: [...exportsKey, 'page', { page, size }],
    queryFn: () => listExports(request, { page, size }),
    placeholderData: keepPreviousData,
    // Follow jobs while vms-worker builds them.
    refetchInterval: (q) =>
      q.state.data?.items.some((e) => e.status === 'queued' || e.status === 'running')
        ? 3000
        : false,
  });

  const download = useMutation({
    mutationFn: (e: RecordingExport) => downloadLink(request, e.id),
    onSuccess: (link) => {
      browser.open(link.url);
    },
    onError: () => {
      notify('Não foi possível iniciar o download. O arquivo pode ter expirado.', 'error');
    },
  });

  if (exports.isError && !exports.data) {
    return (
      <div className="rounded-lg border border-line bg-surface">
        <EmptyState
          tone="crit"
          icon={CircleAlert}
          title="Não foi possível carregar as exportações"
          description="Verifique a conexão com o servidor e tente novamente."
          action={
            <Button variant="primary" onClick={() => void exports.refetch()}>
              Tentar novamente
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      {exports.data?.items.length === 0 ? (
        <EmptyState
          icon={FileArchive}
          title="Nenhuma exportação"
          description="Na tela de Reprodução, use “Exportar” para gerar um arquivo com os vídeos originais e seus códigos SHA-256."
        />
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[860px] text-left text-[13px]"
            aria-busy={exports.isFetching}
          >
            <caption className="sr-only">Exportações de gravação da organização</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-2 font-medium">
                  Câmera e intervalo
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Motivo
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Solicitante
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Arquivo
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Situação
                </th>
                <th scope="col" className="w-px px-4 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!exports.data
                ? Array.from({ length: 4 }, (_, i) => (
                    <tr key={i} aria-hidden="true" className="border-b border-line last:border-0">
                      {['w-48', 'w-40', 'w-24', 'ml-auto w-16', 'w-16', 'w-10'].map((w) => (
                        <td key={w} className="px-4 py-3.5">
                          <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                        </td>
                      ))}
                    </tr>
                  ))
                : exports.data.items.map((e) => {
                    const view = statusView[e.status];
                    return (
                      <tr
                        key={e.id}
                        className="border-b border-line align-top last:border-0 hover:bg-surface-hover/50"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-fg">
                            {e.device.name || (
                              <span className="text-fg-subtle">Câmera excluída</span>
                            )}
                          </div>
                          <div className="font-mono text-xs text-fg-subtle">
                            {formatRange(e.from, e.to, tz)}
                          </div>
                        </td>
                        <td className="max-w-xs px-4 py-2.5 text-fg-muted">
                          <p className="line-clamp-2 break-words">{e.reason}</p>
                        </td>
                        <td className="px-4 py-2.5 text-fg-muted">
                          <div>{e.created_by.name}</div>
                          <div className="text-xs text-fg-subtle">{fmt.absolute(e.created_at)}</div>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                          {e.status === 'ready' || e.status === 'expired' ? (
                            <>
                              <div>
                                {e.format.toUpperCase()} · {formatBytes(e.size_bytes)}
                              </div>
                              <div className="text-fg-subtle">
                                {e.segments} {e.segments === 1 ? 'vídeo' : 'vídeos'}
                              </div>
                              {e.sha256 && (
                                <div className="text-fg-subtle" title={`SHA-256 ${e.sha256}`}>
                                  SHA-256 {e.sha256.slice(0, 12)}…
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-fg-subtle">{e.format.toUpperCase()}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge tone={view.tone} pulse={view.pulse ?? false}>
                            {view.label}
                          </StatusBadge>
                          {e.status === 'failed' && (
                            <p className="mt-1 max-w-[14rem] text-xs text-fg-subtle">
                              {exportFailure(e.error_code)}
                            </p>
                          )}
                          {e.status === 'ready' && e.expires_at && (
                            <p className="mt-1 text-xs text-fg-subtle">
                              até {fmt.absolute(e.expires_at)}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {e.status === 'ready' && canDownload && (
                            <Button
                              className="h-7"
                              aria-label={`Baixar ${e.file_name}`}
                              disabled={download.isPending}
                              onClick={() => {
                                download.mutate(e);
                              }}
                            >
                              <Download aria-hidden="true" className="size-3.5" />
                              Baixar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
      {exports.data && exports.data.total > 0 && (
        <Pagination
          page={page}
          size={size}
          total={exports.data.total}
          onPage={(p) => {
            update({ page: String(p) });
          }}
          onSize={(s) => {
            update({ size: String(s) });
          }}
        />
      )}
    </div>
  );
}
